/*
 * Kroneks — dynamic "Избери модел" (choose model) swatches.
 *
 * Watch models live in the store as separate products (one per colour/strap). A curated
 * `custom.siblings` metafield links a few families and the theme renders those swatches
 * server-side. This script fills the gap for every OTHER watch: it works out the model
 * family from the product's model number (SKU) and finds the sibling products through
 * Shopify's own storefront search, then renders the same swatch UI.
 *
 * It only runs where there is no curated metafield, never writes or stores catalog data,
 * and degrades silently (no swatches) if nothing is found. Grouping rules mirror the
 * offline analysis and are deliberately conservative — they never group unrelated watches.
 */
(function () {
  'use strict';

  // Model number -> family identifier. Two watches are siblings iff these match.
  function familyKey(sku, vendor) {
    var s = (sku || '').trim().toUpperCase();
    if (!s) return null;
    switch (vendor) {
      case 'Casio': {
        // <line>-<model><case letters>-<dial><series><region>, e.g. MTP-VD01 D -1 B VUDF
        var idx = -1;
        for (var i = 0; i < s.length - 1; i++) {
          if (s[i] === '-' && s[i + 1] >= '0' && s[i + 1] <= '9') idx = i;
        }
        if (idx < 0) return 'C|' + s;
        var stem = s.slice(0, idx);          // MTP-VD01D
        var tail = s.slice(idx + 1);          // 1BVUDF
        var model = stem.replace(/(\d)[A-Z]{1,3}$/, '$1'); // strip case letters -> MTP-VD01
        var m = tail.match(/^\d+([A-Z])/);    // series letter after the dial digit(s)
        return 'C|' + model + '|' + (m ? m[1] : '');
      }
      case 'Orient':
      case 'Orient Star':
        // strip trailing <serial digit><colour letter>, e.g. RA-WJ0005L -> RA-WJ000
        return 'O|' + s.replace(/\d[A-Z]\d?$/, '');
      case 'Seiko':
        // conservative: strip only the region code (P1/K1/J1) so distinct models never merge
        return 'SK|' + s.replace(/[A-Z]\d$/, '');
      case 'Guess': {
        var g = s.match(/^([A-Z]{1,3}\d{3,5}[A-Z])\d+$/); // GW0803G2 -> GW0803G
        return 'G|' + (g ? g[1] : s);
      }
      case 'Q&Q': {
        // 1–2 leading letters: V22A-001VY, A05A-003PY, VS54J010Y (VS = 2-letter line)
        var q = s.match(/^([A-Z]{1,2}\d{2}[A-Z])-?\d{3}([A-Z]{1,3})$/); // VS54J010Y -> VS54J | Y
        return 'Q|' + (q ? q[1] + '|' + q[2] : s);
      }
      case 'Citizen': {
        var c = s.match(/^([A-Z]{2}\d{4})-(\d{2})/); // AW0130-85LC -> AW0130-85
        return 'CI|' + (c ? c[1] + '-' + c[2] : s);
      }
      case 'Sekonda': {
        var k = s.match(/^(S-\d+)(?:\.\d+)?$/); // S-1110.30 -> S-1110
        return 'S|' + (k ? k[1] : s);
      }
      case 'Accurist':
        return 'A|' + s; // numbers are distinct models -> effectively standalone
      case 'Fossil':
      case 'Armani Exchange':
      case 'Diesel':
      case 'Skagen':
      case 'Michael Kors': {
        // <letters><3-digit model><1-digit colourway><optional SET/IE/LE...>,
        // e.g. FS6097 -> FS609 (colourway 7). Grouping by the first 3 digits keeps
        // a model's colours together without merging the whole FSxx00-FSxx99 range
        // (which spans dozens of unrelated models).
        var f = s.match(/^([A-Z]+)(\d{3})\d([A-Z]*)$/);
        return 'F|' + (f ? f[1] + f[2] : s);
      }
      case 'Cover':
      case 'Swiss Military By Chrono': {
        // <model>.<colourway>, e.g. CO203.04, SM30200.02 -> model before the dot
        var di = s.indexOf('.');
        return 'D|' + (di > 0 ? s.slice(0, di) : s);
      }
      case 'EPOS': {
        // <a>.<b>.<variant...>, model = first two groups (3435.313.20.15.30 -> 3435.313)
        var ep = s.split('.');
        return 'E|' + (ep.length >= 3 ? ep[0] + '.' + ep[1] : s);
      }
      case 'Ferro': {
        // <model>-<colourway>, e.g. FM40177A-A, FM40177A-A2, FL21823A-D8 -> model
        // before the LAST dash (the colourway code never contains one).
        var fe = s.lastIndexOf('-');
        return 'FE|' + (fe > 0 ? s.slice(0, fe) : s);
      }
      default:
        return (vendor || '?') + '|' + s;
    }
  }

  // Fashion brands whose SKU numbers do NOT encode the model (numbers run
  // sequentially across unrelated models). Their real model name lives in the
  // product image filename, so we group by that instead of by SKU digits.
  var IMAGE_GROUPED = {
    'Fossil': 1, 'Armani Exchange': 1, 'Diesel': 1, 'Skagen': 1, 'Michael Kors': 1
  };

  // Model identity from the import image filename, e.g.
  // ".../bp-Fossil-EVERETT-FS6090-n1.jpg?v=..&width=200" -> "BP-FOSSIL-EVERETT".
  // Drops the image index (-n1) and the trailing SKU token(s) (anything with a digit),
  // leaving the constant brand+model prefix shared by every colourway of a model.
  function imageModel(url) {
    if (!url) return null;
    var file = url.split('?')[0].split('/').pop().replace(/\.[a-z0-9]+$/i, '');
    var parts = file.split('-');
    while (parts.length > 1 && /\d/.test(parts[parts.length - 1])) parts.pop();
    var key = parts.join('-').toUpperCase();
    return key.length >= 3 ? key : null;
  }

  // The key two products must share to be siblings. Fashion brands use the image
  // model name; everyone else uses the SKU-derived familyKey.
  function groupKey(sku, vendor, image) {
    if (IMAGE_GROUPED[vendor]) {
      var im = imageModel(image);
      if (im) return 'IMG|' + vendor + '|' + im;
    }
    return familyKey(sku, vendor);
  }

  // Broad search query guaranteed to return every member of the family (filtered later).
  function searchStem(sku, vendor) {
    var s = (sku || '').trim().toUpperCase();
    if (!s) return '';
    switch (vendor) {
      case 'Casio': {
        var idx = -1;
        for (var i = 0; i < s.length - 1; i++) {
          if (s[i] === '-' && s[i + 1] >= '0' && s[i + 1] <= '9') idx = i;
        }
        var stem = idx < 0 ? s : s.slice(0, idx);
        return stem.replace(/(\d)[A-Z]{1,3}$/, '$1'); // MTP-VD01
      }
      case 'Orient':
      case 'Orient Star':
        return s.replace(/\d[A-Z]\d?$/, '');          // RA-WJ000
      case 'Seiko':
        return s.replace(/[A-Z]\d$/, '');             // SSB429
      case 'Guess': {
        var g = s.match(/^([A-Z]{1,3}\d{3,5}[A-Z])\d+$/);
        return g ? g[1] : s;                          // GW0803G
      }
      case 'Q&Q': {
        var q = s.match(/^([A-Z]{1,2}\d{2}[A-Z])/);
        return q ? q[1] : s;                          // V22A / VS54J
      }
      case 'Citizen': {
        var c = s.match(/^([A-Z]{2}\d{4}-\d{2})/);
        return c ? c[1] : s;                          // AW0130-85
      }
      case 'Sekonda': {
        var k = s.match(/^(S-\d+)/);
        return k ? k[1] : s;                          // S-1110
      }
      case 'Fossil':
      case 'Armani Exchange':
      case 'Diesel':
      case 'Skagen':
      case 'Michael Kors': {
        // Broad neighbourhood: these brands' colourways scatter across a model's
        // SKU range, so search the first-2-digit block and let the image model
        // name (groupKey) do the exact grouping. FS6097 -> FS60*
        var f = s.match(/^([A-Z]+\d{2})/);
        return f ? f[1] : s;                          // FS60
      }
      case 'Cover':
      case 'Swiss Military By Chrono': {
        var di = s.indexOf('.');
        return di > 0 ? s.slice(0, di) : s;           // CO203 / SM30200
      }
      case 'EPOS': {
        return s.split('.')[0] || s;                  // 3435 (broad; familyKey narrows)
      }
      case 'Ferro': {
        var fe = s.lastIndexOf('-');
        return fe > 0 ? s.slice(0, fe) : s;           // FM40177A
      }
      default:
        return s;
    }
  }

  function attr(str) { return String(str == null ? '' : str).replace(/"/g, '&quot;'); }

  function swatchHtml(member, isActive) {
    var img = '<img src="' + attr(member.image) + '" alt="' + attr(member.title) + '" loading="lazy">';
    var soldout = member.available === false;
    var cls = 'sibling-swatch' + (isActive ? ' active' : '') + (soldout ? ' sibling-swatch--soldout' : '');
    if (isActive || soldout) {
      // current product, or out of stock -> render as a non-link (unclickable)
      return '<div class="' + cls + '" data-title="' + attr(member.title) + '">' + img + '</div>';
    }
    return '<a href="' + attr(member.url) + '" class="' + cls + '" data-title="' + attr(member.title) + '">' + img + '</a>';
  }

  function render(root, members, selfHandle) {
    members.sort(function (a, b) { return a.handle < b.handle ? -1 : (a.handle > b.handle ? 1 : 0); });
    var list = root.querySelector('.sibling-swatches-list');
    if (!list) return;
    list.innerHTML = members.map(function (m) { return swatchHtml(m, m.handle === selfHandle); }).join('');
    root.removeAttribute('hidden');
  }

  function hoverDelegation() {
    var label = document.getElementById('watch-model-name');
    if (!label) return;
    document.addEventListener('mouseover', function (e) {
      var sw = e.target.closest && e.target.closest('.sibling-swatch');
      if (sw) label.textContent = sw.getAttribute('data-title') || '';
    });
    document.addEventListener('mouseout', function (e) {
      var sw = e.target.closest && e.target.closest('.sibling-swatch');
      if (sw) label.textContent = '';
    });
  }

  function init() {
    hoverDelegation(); // works for curated (server-rendered) and dynamic swatches alike

    var root = document.querySelector('[data-sibling-root][data-fallback]');
    if (!root) return; // curated product, or no swatch block on this page

    var sku = (root.dataset.sku || '').trim().toUpperCase();
    var vendor = (root.dataset.vendor || '').trim();
    var selfHandle = root.dataset.handle;
    if (!sku || !vendor) return;

    var fk = groupKey(sku, vendor, root.dataset.image);
    var stem = searchStem(sku, vendor);
    if (!fk || !stem) return;

    // Append a trailing '*' so Shopify prefix-matches the model stem. Many colour variants
    // carry an extra case letter right after the model number (e.g. "MDV-107D-1A2VDF" next to
    // "MDV-107-1A3VEF"); Shopify indexes "MDV-107D" as a different token from "MDV-107", so a
    // plain query returns only part of the family and the swatches fragment across pages. The
    // wildcard returns the whole family; familyKey() below still decides the actual grouping.
    var url = '/search?type=product&view=siblings&options[prefix]=last' +
      '&options[unavailable_products]=last&q=' + encodeURIComponent(stem + '*');

    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.products) return;
        var seen = {};
        var members = [];
        data.products.forEach(function (p) {
          var psku = (p.sku || '').trim().toUpperCase();
          if (!psku || !p.handle || seen[p.handle]) return;
          if (p.vendor !== vendor) return;
          if (!p.image) return;
          if (groupKey(psku, vendor, p.image) !== fk) return;
          seen[p.handle] = true;
          members.push(p);
        });
        if (!seen[selfHandle]) {
          members.push({
            handle: selfHandle,
            title: root.dataset.title || '',
            url: root.dataset.url || ('/products/' + selfHandle),
            image: root.dataset.image || '',
            available: root.dataset.available !== 'false'
          });
        }
        if (members.length < 2) return; // self + at least one sibling, else "not applicable"
        render(root, members, selfHandle);
      })
      .catch(function () { /* search unavailable -> no swatches, page unaffected */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
